import React, { useState, useEffect } from "react";
import { Form, Input, Button, Select, message, Modal, Table, Spin, InputNumber } from "antd";
import AxiosOICStat from "AxiosOICStat";
import { useSelector } from "react-redux";
import { getIndicatorName } from "../utils/indicatorName";

const { Option } = Select;

export function GenerateTable() {
  const [pdfOptionsForm] = Form.useForm();
  const [configurations, setConfigurations] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const indicatorsState = useSelector((state) => state.indicators);
  const [csvContent, setCsvContent] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isPdfModalVisible, setIsPdfModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [isWordSelected, setIsWordSelected] = useState(false);
  const [isPdfSelected, setIsPdfSelected] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [msgApi, contextHolder] = message.useMessage();
  
  // New state for the PDF options modal (font family and text alignment)
  const [pdfOptionsModalVisible, setPdfOptionsModalVisible] = useState(false);


  const downloadCsv = () => {
    const link = document.createElement("a");
    link.href = downloadUrl;
    const fileName = getIndicatorName(indicatorsState.value[selectedIndicator]) || "report";
    link.setAttribute("download", `${fileName}.csv`); // Set the filename
    document.body.appendChild(link);
    link.click();

    // Cleanup
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  };


  console.log("indicatorsState", indicatorsState.value);

  // Fetch existing configurations
  const fetchConfigurations = async () => {
    try {
      const response = await AxiosOICStat.get("/syb/syb_configs/");
      setConfigurations(response.data);
    } catch (error) {
      message.error("Failed to fetch configurations: " + error.message);
    }
  };

  // Fetch existing indicators
  const fetchIndicators = async () => {
    try {
      const response = await AxiosOICStat.get("/syb/syb_indicators/");
      setIndicators(response.data);
    } catch (error) {
      message.error("Failed to fetch indicators: " + error.message);
    }
  };

  useEffect(() => {
    fetchConfigurations();
    fetchIndicators();
  }, []);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
  
      const response = await AxiosOICStat.post("/syb/generate_tables_csv/", values, {
        responseType: "blob",
      });
  
      setSelectedIndicator(values.ind_code);
      setLoading(false);
  
      const fileReader = new FileReader();
      fileReader.onload = () => {
        const csvText = fileReader.result;
  
        const rows = csvText.split("\n").map((row) => {
          return row.split(",").map((cell, index) => {
            const trimmedCell = cell.trim().replace(/^"|"$/g, "");
  
            const numericValue = Number(trimmedCell.replace(/,/g, ""));
  
            if (!isNaN(numericValue) && numericValue > 1000 && !/^\d{4}$/.test(trimmedCell)) {
              return numericValue.toLocaleString();
            }
  
            return trimmedCell;
          });
        });
  
        setCsvContent(rows);
        setIsModalVisible(true);
      };
      fileReader.readAsText(new Blob([response.data], { type: "text/csv" }));
  
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
      setDownloadUrl(url);
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || error.message || "Failed to generate report.";
      message.error(errorMessage);
    }
  };
  
  const handlePdfGeneration = async (values) => {

    if(isPdfSelected){
      try {
        setLoading(true);
        setFileLoading(true);
        console.log("Generating PDF with values:", values);
        // Request to generate the PDF, including font options in the payload
        const response = await AxiosOICStat.post("/syb/generate_tables_pdf/", values, {
          responseType: "blob", // Ensure response is treated as a binary file
        });
        setLoading(false);
        setFileLoading(false);
    
        // Create a Blob URL for preview
        const pdfBlobUrl = window.URL.createObjectURL(
          new Blob([response.data], { type: "application/pdf" })
        );
        setPdfUrl(pdfBlobUrl);
        setIsPdfModalVisible(true); // Show PDF modal
      } catch (error) {
        setLoading(false);
        const errorMessage =
          error.response?.data?.detail || error.message || "Failed to generate PDF.";
        message.error(errorMessage);
      }
    }

    else if (isWordSelected) {
      try {
        setLoading(true);
        setFileLoading(true);
        console.log("Generating Word with values:", values);
        const response = await AxiosOICStat.post("/syb/generate_tables_word/", values, {
          responseType: "arraybuffer",
        });
        setLoading(false);
        setFileLoading(false);
    
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        const ind_name = getIndicatorName(indicatorsState.value[values.ind_code]) || "Unknown Indicator";
        link.href = url;
        link.setAttribute("download", `${values.ind_code}-${ind_name}.docx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        setLoading(false);
        const errorMessage =
          error.response?.data?.detail || error.message || "Failed to generate DOCX.";
        message.error(errorMessage);
      }
    }    
    else{
      message.error("Please select a file type to generate");
    }
  };

  return (
    <div>
      <h1>Generate SYB</h1>
      <Form
        layout="vertical"
        onFinish={(values) => {
          console.log("Form submitted with values:", values);
          handleSubmit(values);
        }}
        onFinishFailed={(errorInfo) => {
          console.log("Form submission failed with errors:", errorInfo);
        }}
      >
        <Form.Item
          name="ind_code"
          label="Indicator"
          rules={[{ required: true, message: "Please select an indicator" }]}
        >
          <Select
            placeholder="Select Indicator"
            showSearch
            autoClearSearchValue={false}
            optionFilterProp="children"
            filterOption={(input, option) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          >
            {indicators.map((indicator) => (
              <Option
                key={indicator.ind_code}
                value={indicator.ind_code}
                label={`${indicator.ind_code} - ${
                  getIndicatorName(indicatorsState.value[indicator.ind_code]) || "Unknown Indicator"
                }`}
              >
                {indicator.ind_code} -{" "}
                {getIndicatorName(indicatorsState.value[indicator.ind_code]) || "Unknown Indicator"}
              </Option>
            ))}
          </Select>
        </Form.Item>
  
        <Form.Item>
          <Spin spinning={loading}>
            <Button
              type="primary"
              htmlType="submit"
              onClick={() => {
                console.log("Submit button clicked");
              }}
            >
              Generate Report
            </Button>
          </Spin>
        </Form.Item>
      </Form>
  
      <Modal
  title="Preview CSV Content"
  visible={isModalVisible}
  onCancel={() => setIsModalVisible(false)}
  footer={[
    <Button
      key="convert-to-word"
      type="primary"
      onClick={() => {
        setIsWordSelected(true);
        setIsPdfSelected(false);
        setPdfOptionsModalVisible(true);
      }}
    >
      Convert to Word
    </Button>,
    <Button
      key="convert-to-pdf"
      type="primary"
      disabled={true}
      onClick={() => {
        setIsPdfSelected(true);
        setIsWordSelected(false);
        setPdfOptionsModalVisible(true);
      }}
    >
      Convert to PDF
    </Button>,
    <Button key="download" type="primary" onClick={() => downloadCsv()}>
      Download CSV
    </Button>,
    <Button key="close" onClick={() => setIsModalVisible(false)}>
      Close
    </Button>,
  ]}
  width={800}
  bodyStyle={{ maxHeight: "60vh", overflow: "auto" }}
>
  <Spin spinning={fileLoading}>
    {csvContent.length > 0 ? (
      <Table
        dataSource={(() => {
          const aggregationRows = [];
          const normalRows = [];
          csvContent.slice(1).forEach((row, index) => {
            const rowData = {
              key: index,
              ...row.reduce(
                (acc, col, i) => ({ ...acc, [csvContent[0][i]]: col }),
                {}
              ),
            };
            if (
              rowData["Country Name"] === "OIC" ||
              rowData["Country Name"] === "World"
            ) {
              aggregationRows.push(rowData);
            } else {
              normalRows.push(rowData);
            }
          });
          return [...normalRows, ...aggregationRows];
        })()}
        columns={csvContent[0].map((header) => ({
          title: header,
          dataIndex: header,
        }))}
        pagination={false}
        bordered
      />
    ) : (
      <p>No data to display</p>
    )}
  </Spin>
</Modal>

  
      {/* New modal for PDF options: Font Family and Text Alignment */}
      <Modal
        title="Select PDF Options"
        visible={pdfOptionsModalVisible}
        onCancel={() => setPdfOptionsModalVisible(false)}
        footer={null}
      >
        <Form
          form={pdfOptionsForm}
          layout="vertical"
          initialValues={{ font_family: "Microsoft Sans Serif", text_alignment: "right" }}
          onFinish={(pdfOptions) => {
            // Combine the selected indicator with the PDF options
            const values = { ind_code: selectedIndicator, ...pdfOptions };
            setPdfOptionsModalVisible(false);
            handlePdfGeneration(values);
          }}
        >
          <Form.Item
            name="font_family"
            label="Font Family"
            rules={[{ required: true, message: "Please select a font family" }]}
          >
            <Select placeholder="Select Font Family"> 
              <Option value="Microsoft Sans Serif">Microsoft Sans Serif</Option>
              <Option value="Arial">Arial</Option>
              <Option value="Times New Roman">Times New Roman</Option>
              <Option value="Courier New">Courier New</Option>
              <Option value="Calibri Light">Calibri Light</Option>
              {/* Add other font options as needed */}
            </Select>
          </Form.Item>
          <Form.Item
            name="text_alignment"
            label="Text Alignment"
            rules={[{ required: true, message: "Please select a text alignment" }]}
          >
            <Select placeholder="Select Text Alignment">
              <Option value="left">Left</Option>
              <Option value="center">Center</Option>
              <Option value="right">Right</Option>
            </Select>
          </Form.Item>
          <Form.Item
        name="font_size"
        label="Font Size"
        rules={[{ required: false, message: "Please enter a font size" }]}
      >
        <InputNumber style={{ width: "100%" }} min={1} />
      </Form.Item>

      <Form.Item
        name="custom_table_number"
        label="Custom Table Number"
        rules={[{ required: false, message: "You can enter a custom table number" }]}
      >
        <Input placeholder="1.1, 1.2" />
      </Form.Item>

      <Form.Item
        name="custom_table_title"
        label="Custom Table Title"
        rules={[{ required: false, message: "You can enter a custom table title" }]}
      >
        <Input placeholder="Table x.x: Indicator Name" />
      </Form.Item>

      <Form.Item
        name="header_height"
        label="Header Height"
        rules={[{ required: false, message: "Please enter header height" }]}
      >
        <InputNumber style={{ width: "100%" }} min={1} />
      </Form.Item>

      <Form.Item
        name="row_height"
        label="Row Height"
        rules={[{ required: false, message: "Please enter row height" }]}
      >
        <InputNumber style={{ width: "100%" }} min={1} />
      </Form.Item>

      <Form.Item
        name="header_fill_color"
        label="Header Fill Color (R,G,B)"
        rules={[{ required: false, message: "Please enter header fill color" }]}
      >
        <Input placeholder="e.g., 0,70,132" />
      </Form.Item>

      <Form.Item
        name="table_title_font_size"
        label="Table Title Font Size"
        rules={[{ required: false, message: "Please enter table title font size" }]}
      >
        <Input placeholder="10" />
      </Form.Item>

      <Form.Item
        name="header_text_color"
        label="Header Text Color (R,G,B)"
        rules={[{ required: false, message: "Please enter header text color" }]}
      >
        <Input placeholder="e.g., 255,255,255" />
      </Form.Item>

      <Form.Item
        name="row_fill_color"
        label="Row Fill Color (R,G,B)"
        rules={[{ required: false, message: "Please enter row fill color" }]}
      >
        <Input placeholder="e.g., 217,225,242" />
      </Form.Item>

      <Form.Item
        name="bold_line_thickness"
        label="Bold Line Thickness"
        rules={[{ required: false, message: "Please enter bold line thickness" }]}
      >
        <InputNumber style={{ width: "100%" }} min={0.1} step={0.1} />
      </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Submit
            </Button>
          </Form.Item>
        </Form>
      </Modal>
  
      <Modal
        title="Preview PDF Report"
        visible={isPdfModalVisible}
        onCancel={() => setIsPdfModalVisible(false)}
        footer={[
          <Button
            key="download-pdf"
            type="primary"
            disabled={true}
            onClick={() => {
              const link = document.createElement("a");
              link.href = pdfUrl;
              link.setAttribute("download", "report.pdf");
              document.body.appendChild(link);
              link.click();
              link.remove();
            }}
          >
            Download PDF
          </Button>,
          <Button key="close-pdf" onClick={() => setIsPdfModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
        style={{ maxHeight: "80vh", overflow: "auto" }}
      >
        {pdfUrl ? (
          <iframe
            src={pdfUrl}
            title="PDF Preview"
            style={{ width: "100%", height: "60vh" }}
          ></iframe>
        ) : (
          <Spin spinning={loading} />
        )}
      </Modal>
  
    </div>
  );
}
